import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Transaction, TransactionFilters } from '../../../types';
import { TransactionList } from './TransactionList';
import { PageHeader } from '@kioskgaming/ui';
import {
  buildTransactionsListSearch,
  buildUrlSearchParams,
  parseBuyerScopeFromUrl,
} from './transactionListUrl';

type LocalTransactionFilters = Omit<TransactionFilters, 'search' | 'buyerScope'>;

const TransactionsManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const buyerScope = parseBuyerScopeFromUrl(searchParams.get('scope'));
  const search = searchParams.get('search') || undefined;

  const [localFilters, setLocalFilters] = useState<LocalTransactionFilters>({
    page: 1,
    limit: 20,
    type: undefined,
    status: undefined,
  });

  // Reset page when list query in the URL changes (deep link, menu, back button).
  useEffect(() => {
    setLocalFilters((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [search, buyerScope]);

  const filters: TransactionFilters = useMemo(
    () => ({
      ...localFilters,
      buyerScope,
      search,
    }),
    [localFilters, buyerScope, search],
  );

  const setFilters = useCallback(
    (updater: React.SetStateAction<TransactionFilters>) => {
      setLocalFilters((prevLocal) => {
        const current: TransactionFilters = {
          ...prevLocal,
          buyerScope,
          search,
        };
        const next = typeof updater === 'function' ? updater(current) : updater;

        const nextScope = next.buyerScope || 'user';
        const nextSearch = next.search?.trim() || undefined;
        if (nextScope !== buyerScope || nextSearch !== search) {
          setSearchParams(buildUrlSearchParams(next.buyerScope, next.search), { replace: true });
        }

        return {
          page: next.page,
          limit: next.limit ?? prevLocal.limit ?? 20,
          type: next.type,
          status: next.status,
          email: next.email,
          phone: next.phone,
          provider: next.provider,
          sortBy: next.sortBy,
          sortOrder: next.sortOrder,
        };
      });
    },
    [buyerScope, search, setSearchParams],
  );

  const handleViewTransaction = (transaction: Transaction) => {
    const listSearch = buildTransactionsListSearch({
      buyerScope: filters.buyerScope,
      search: filters.search,
    });
    navigate(`/transactions/${transaction.id}${listSearch}`);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payment transactions"
        description={
          <>
            Gateway, shop counter (deposit/redeem), and payment-provider records. Shop counter
            transactions use provider <span className="font-medium">Shop player cash</span>.
          </>
        }
      />

      <TransactionList
        filters={filters}
        setFilters={setFilters}
        onViewTransaction={handleViewTransaction}
      />
    </div>
  );
};

export default TransactionsManagement;
